import React, { useMemo, useRef, useState } from "react";
import { addDoc, collection, query } from "firebase/firestore";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import MainLayout from "../../layouts/main_layout/main_layout";
import { useFeedback } from "../../providers/feedback/feedback";
import { MAIN_THEME_COLOR, SECONDARY_THEME_COLOR } from "../../providers/theme/colors/colors";
import type { DeaneryOption, ParishOption } from "../../utils/deanery_parishes";
import { parishListMap } from "../../utils/deanery_parishes";
import DycConfirmationModal from "./components/dyc_confirmation_modal";
import DycEventForms from "./components/dyc_event_forms";
import DycInfoPanel from "./components/dyc_info_panel";
import DycRegistrationForm from "./components/dyc_registration_form";
import DycSubmissionSuccessModal from "./components/dyc_submission_success_modal";
import DycWarningModal from "./components/dyc_warning_modal";
import {
  defaultEventForms,
  type EventFormState,
  type RegistrationEventId,
} from "./data";
import {
  generateDycRegistrationPdf,
  type MainRegistrationPdfData,
} from "./utils/generateDycRegistrationPdf";
import { sendReceiptEmail } from "./utils/sendReceiptEmail";

type RegistrationRecord = {
  parish?: { id?: number; value?: string; name?: string };
  isPitchBattleEntry?: boolean;
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildMainEventPayload = (eventForms: EventFormState): Record<RegistrationEventId, unknown> => ({
  spotlight: {
    performingEventName: eventForms.spotlight.performingEventName.trim(),
    entryName: eventForms.spotlight.performingEventName.trim(),
    eventDescription: eventForms.spotlight.eventDescription.trim(),
    participantCount: Number(eventForms.spotlight.participantCount || "0"),
    participants: eventForms.spotlight.participantNames
      .slice(0, Number(eventForms.spotlight.participantCount || "0"))
      .map((name) => name.trim())
      .filter(Boolean),
    teamLeadContact: eventForms.spotlight.teamLeadContact,
  },
  jam: {
    participantCount: 1,
    participants: [eventForms.jam.participantName.trim()].filter(Boolean),
    participantContact: eventForms.jam.participantContact,
  },
  ecomosaic: {
    participantCount: eventForms.ecomosaic.hasSecondParticipant ? 2 : 1,
    participants: [
      eventForms.ecomosaic.primaryName.trim(),
      eventForms.ecomosaic.hasSecondParticipant
        ? eventForms.ecomosaic.secondaryName.trim()
        : "",
    ].filter(Boolean),
    contacts: [
      eventForms.ecomosaic.primaryContact,
      eventForms.ecomosaic.hasSecondParticipant
        ? eventForms.ecomosaic.secondaryContact
        : "",
    ].filter(Boolean),
  },
  faithvault: {
    participantCount: 3,
    participants: eventForms.faithvault.memberNames.map((name) => name.trim()).filter(Boolean),
    teamLeadContact: eventForms.faithvault.teamLeadContact,
  },
  pitchbattle: {
    confirmed: true,
  },
  rhythmroots: {
    participantCount: Number(eventForms.rhythmroots.participantCount || "0"),
    participants: eventForms.rhythmroots.participantNames
      .slice(0, Number(eventForms.rhythmroots.participantCount || "0"))
      .map((name) => name.trim())
      .filter(Boolean),
  },
});

export default function DycYuvaDabazoPage() {
  const firestore = useFirestore();
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const [selectedDeanery, setSelectedDeanery] = useState<DeaneryOption | null>(null);
  const [selectedParish, setSelectedParish] = useState<ParishOption | null>(null);
  const [presidentName, setPresidentName] = useState("");
  const [animatorName, setAnimatorName] = useState("");
  const [presidentContactNumber, setPresidentContactNumber] = useState("");
  const [animatorContactNumber, setAnimatorContactNumber] = useState("");
  const [presidentEmail, setPresidentEmail] = useState("");
  const [totalParticipantsCount, setTotalParticipantsCount] = useState("");
  const [selectedEvents, setSelectedEvents] = useState<RegistrationEventId[]>(["rhythmroots"]);
  const [eventForms, setEventForms] = useState<EventFormState>(defaultEventForms);

  const registrationFormRef = useRef<HTMLDivElement>(null);
  const paymentDetailsRef = useRef<HTMLDivElement>(null);
  const [activeOnlineEvent, setActiveOnlineEvent] = useState(0);

  // Payment Details state
  const [paymentPayerName, setPaymentPayerName] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentScreenshot, setPaymentScreenshot] = useState("");
  const [paymentScreenshotName, setPaymentScreenshotName] = useState("");

  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showNoEventsWarningModal, setShowNoEventsWarningModal] = useState(false);
  const [showOver100WarningModal, setShowOver100WarningModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState<{
    open: boolean;
    blobUrl: string;
    fileName: string;
    pdfDataUrl?: string;
    parishName: string;
    recipientEmail: string;
  }>({
    open: false,
    blobUrl: "",
    fileName: "",
    parishName: "",
    recipientEmail: "",
  });

  const registrationQuery = query(collection(firestore, "dyc_yuva_dabazo"));
  const { data: registrations } = useFirestoreCollectionData<RegistrationRecord>(registrationQuery);

  const parishOptions = useMemo(
    () => (selectedDeanery ? parishListMap[selectedDeanery.name] || [] : []),
    [selectedDeanery],
  );

  const handleToggleEventSelection = (eventId: RegistrationEventId) => {
    setSelectedEvents((current) =>
      current.includes(eventId)
        ? current.filter((value) => value !== eventId)
        : [...current, eventId],
    );
  };

  const handleOpenPitchBattle = () => {
    navigate("/yuvadabazo/pitch-battle", {
      state: {
        selectedDeanery,
        selectedParish,
        presidentName,
        animatorName,
        presidentContactNumber,
        animatorContactNumber,
        presidentEmail,
      },
    });
  };

  const updateEventForms = (updater: (current: EventFormState) => EventFormState) => {
    setEventForms((current) => updater(current));
  };

  const validateMainForm = () => {
    if (!selectedDeanery || !selectedParish) {
      return "Please select the deanery and parish.";
    }
    if (!presidentName.trim() || !animatorName.trim()) {
      return "President name and animator name are required.";
    }
    if (presidentContactNumber.length !== 10 || animatorContactNumber.length !== 10) {
      return "Both contact numbers must be 10 digits.";
    }
    if (!emailRegex.test(presidentEmail.trim())) {
      return "Please enter a valid president email address.";
    }
    if (!totalParticipantsCount.trim() || isNaN(Number(totalParticipantsCount)) || Number(totalParticipantsCount) < 1) {
      return "Total participants count must be at least 1.";
    }

    // Payment Compulsory Validation
    if (!paymentPayerName.trim()) {
      return "Name of person who made the payment is required.";
    }
    if (!paymentDate) {
      return "Date of payment is required.";
    }
    const amt = Number(paymentAmount);
    if (isNaN(amt) || amt < 250 || amt > 100000) {
      return "Payment amount must be between ₹250 and ₹1,00,000.";
    }
    if (!paymentScreenshot) {
      return "Please upload the payment screenshot.";
    }

    for (const eventId of selectedEvents) {
      if (eventId === "jam") {
        if (
          !eventForms.jam.participantName.trim() ||
          eventForms.jam.participantContact.length !== 10
        ) {
          return "JAM needs one participant name and a valid contact number.";
        }
      }

      if (eventId === "ecomosaic") {
        if (
          !eventForms.ecomosaic.primaryName.trim() ||
          eventForms.ecomosaic.primaryContact.length !== 10
        ) {
          return "Eco - Mosaic needs the first participant name and contact.";
        }
        if (
          eventForms.ecomosaic.hasSecondParticipant &&
          (!eventForms.ecomosaic.secondaryName.trim() ||
            eventForms.ecomosaic.secondaryContact.length !== 10)
        ) {
          return "Eco - Mosaic second participant details are incomplete.";
        }
      }

      if (eventId === "spotlight") {
        const spotlightCount = Number(eventForms.spotlight.participantCount || "0");
        const spotlightNames = eventForms.spotlight.participantNames
          .slice(0, spotlightCount)
          .map((name) => name.trim())
          .filter(Boolean);

        if (
          !eventForms.spotlight.performingEventName.trim() ||
          !eventForms.spotlight.eventDescription.trim() ||
          eventForms.spotlight.teamLeadContact.length !== 10 ||
          spotlightCount < 1 ||
          spotlightCount > 5 ||
          spotlightNames.length !== spotlightCount
        ) {
          return "SpotLight needs the event name, event description, team lead contact, and all participant names.";
        }
      }

      if (eventId === "faithvault") {
        const memberNames = eventForms.faithvault.memberNames
          .map((name) => name.trim())
          .filter(Boolean);
        if (memberNames.length !== 3 || eventForms.faithvault.teamLeadContact.length !== 10) {
          return "The Faith Vault needs all 3 member names and the team lead contact number.";
        }
      }

      if (eventId === "rhythmroots") {
        const rhythmCount = Number(eventForms.rhythmroots.participantCount || "0");
        const rhythmNames = eventForms.rhythmroots.participantNames
          .slice(0, rhythmCount)
          .map((name) => name.trim())
          .filter(Boolean);

        if (
          isNaN(rhythmCount) ||
          rhythmCount < 1 ||
          rhythmCount > 50 ||
          rhythmNames.length !== rhythmCount
        ) {
          return "Rhythm and Roots requires between 1 and 50 participants and all participant names to be filled.";
        }
      }
    }


    const alreadyRegistered = (registrations || []).some(
      (record) =>
        !record.isPitchBattleEntry &&
        (record.parish?.id === selectedParish.id ||
          (record.parish?.value &&
            selectedParish.value &&
            record.parish.value.toLowerCase() === selectedParish.value.toLowerCase())),
    );

    if (alreadyRegistered) {
      const parishName = selectedParish.value || "this parish";
      return `Yuva Dabazo 2026 registration of ${parishName} completed`;
    }

    return "";
  };

  const handleOpenSubmission = () => {
    const validationError = validateMainForm();
    setFormError(validationError);

    if (validationError) {
      showSnackbar(validationError, "#f44336");
      return;
    }

    if (Number(totalParticipantsCount) > 100) {
      setShowOver100WarningModal(true);
      return;
    }

    if (!selectedEvents.length) {
      setShowNoEventsWarningModal(true);
      return;
    }

    setShowConfirmationModal(true);
  };

  const executeSubmit = async () => {
    setIsSubmitting(true);

    try {
      const eventsPayload = buildMainEventPayload(eventForms);
      const formattedPaymentDate = paymentDate.includes("-")
        ? paymentDate.split("-").reverse().join("/")
        : paymentDate;
      const parishName = selectedParish?.value || "parish";

      // Prepare data & generate PDF receipt
      const pdfData: MainRegistrationPdfData = {
        type: "main",
        selectedDeanery,
        selectedParish,
        presidentName,
        presidentContactNumber,
        presidentEmail,
        animatorName,
        animatorContactNumber,
        totalParticipantsCount,
        selectedEvents,
        eventForms,
        paymentPayerName,
        paymentDate,
        paymentAmount,
        paymentScreenshot,
      };

      const { doc: pdfDoc, blobUrl, fileName } = generateDycRegistrationPdf(pdfData);

      await addDoc(collection(firestore, "dyc_yuva_dabazo"), {
        createdAt: Date().toString(),
        updatedAt: Date().toString(),
        deanery: selectedDeanery,
        parish: selectedParish,
        presidentName: presidentName.trim(),
        animatorName: animatorName.trim(),
        presidentContactNumber,
        animatorContactNumber,
        contactNumber: presidentContactNumber,
        email: presidentEmail.trim(),
        totalParticipantsCount: Number(totalParticipantsCount),
        selectedEvents,
        events: Object.fromEntries(
          selectedEvents.map((eventId) => [eventId, eventsPayload[eventId]]),
        ),
        paymentDetails: {
          payerName: paymentPayerName.trim(),
          paymentDate: formattedPaymentDate,
          rawPaymentDate: paymentDate,
          amountPaid: Number(paymentAmount),
          screenshotUrl: paymentScreenshot,
          screenshotName: paymentScreenshotName,
        },
      });

      // Auto-download PDF
      pdfDoc.save(fileName);

      // Automatically email copy to user and organizers
      const currentEmail = presidentEmail.trim();
      sendReceiptEmail({
        recipientEmail: currentEmail,
        parishName,
        deaneryName: selectedDeanery?.name,
        presidentName: presidentName.trim(),
        presidentContactNumber,
        animatorName: animatorName.trim(),
        animatorContactNumber,
        registrationType: "main",
        doc: pdfDoc,
        fileName,
      });

      setFormError("");
      setShowConfirmationModal(false);
      showSnackbar(`Yuva Dabazo 2026 registration of ${parishName} completed`);

      // Open post-submission PDF view/download modal
      setSuccessModalData({
        open: true,
        blobUrl,
        fileName,
        parishName,
        recipientEmail: currentEmail,
      });

      // Reset form fields
      setSelectedParish(null);
      setPresidentName("");
      setAnimatorName("");
      setPresidentContactNumber("");
      setAnimatorContactNumber("");
      setPresidentEmail("");
      setTotalParticipantsCount("");
      setSelectedEvents(["rhythmroots"]);
      setPaymentPayerName("");
      setPaymentDate("");
      setPaymentAmount("");
      setPaymentScreenshot("");
      setPaymentScreenshotName("");
      setEventForms(defaultEventForms);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to submit registration.";
      setFormError(message);
      showSnackbar(message, "#f44336");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <Stack spacing={3.5} my={{ xs: 4, md: 6 }}>
        <Box>
          <Typography variant="h3" fontWeight={900} color={MAIN_THEME_COLOR}>
            DYC - Yuva Dabazo 2026 Registration
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={1}>
            Complete the event registration here. Pitch Battle uses a separate form.{" "}
            <Button
              variant="text"
              onClick={() => {
                registrationFormRef.current?.scrollIntoView({ behavior: "smooth" });
              }}
              sx={{
                p: 0,
                minWidth: 0,
                color: SECONDARY_THEME_COLOR,
                fontWeight: 700,
                textTransform: "none",
                textDecoration: "underline",
                verticalAlign: "baseline",
                display: "inline-flex",
                ml: 0.5,
                "&:hover": {
                  textDecoration: "underline",
                  backgroundColor: "transparent",
                },
              }}
            >
              Click here to register
            </Button>
          </Typography>
          <Box sx={{ display: { xs: "block", md: "none" }, mt: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                registrationFormRef.current?.scrollIntoView({ behavior: "smooth" });
              }}
              sx={{
                py: 1.5,
                borderRadius: 3,
                fontWeight: 700,
                textTransform: "none",
                background: `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
                boxShadow: "0 4px 12px rgba(9, 51, 84, 0.15)",
                color: "#ffffff",
                "&:hover": {
                  background: `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
                }
              }}
            >
              Click here to register
            </Button>
          </Box>
        </Box>

        <DycInfoPanel
          activeOnlineEvent={activeOnlineEvent}
          onOnlineEventChange={setActiveOnlineEvent}
          onOpenPitchBattle={handleOpenPitchBattle}
          formError={formError}
          paymentDetailsRef={paymentDetailsRef}
          onScrollToForm={() => {
            registrationFormRef.current?.scrollIntoView({ behavior: "smooth" });
          }}
        />

        <Box ref={registrationFormRef}>
          <DycRegistrationForm
            selectedDeanery={selectedDeanery}
            selectedParish={selectedParish}
            parishOptions={parishOptions}
            presidentName={presidentName}
            animatorName={animatorName}
            presidentContactNumber={presidentContactNumber}
            animatorContactNumber={animatorContactNumber}
            presidentEmail={presidentEmail}
            totalParticipantsCount={totalParticipantsCount}
            onDeaneryChange={(value) => {
              setSelectedDeanery(value);
              setSelectedParish(null);
            }}
            onParishChange={setSelectedParish}
            onPresidentNameChange={setPresidentName}
            onAnimatorNameChange={setAnimatorName}
            onPresidentContactNumberChange={setPresidentContactNumber}
            onAnimatorContactNumberChange={setAnimatorContactNumber}
            onPresidentEmailChange={setPresidentEmail}
            onTotalParticipantsCountChange={(value) => {
              setTotalParticipantsCount(value);
              if (Number(value) > 100) {
                setShowOver100WarningModal(true);
              }
            }}
            paymentPayerName={paymentPayerName}
            paymentDate={paymentDate}
            paymentAmount={paymentAmount}
            paymentScreenshot={paymentScreenshot}
            paymentScreenshotName={paymentScreenshotName}
            onPaymentPayerNameChange={setPaymentPayerName}
            onPaymentDateChange={setPaymentDate}
            onPaymentAmountChange={setPaymentAmount}
            onPaymentScreenshotChange={(dataUrl, fileName) => {
              setPaymentScreenshot(dataUrl);
              setPaymentScreenshotName(fileName);
            }}
            onScrollToPaymentDetails={() => {
              paymentDetailsRef.current?.scrollIntoView({ behavior: "smooth" });
            }}
          />
        </Box>

        <DycEventForms
          selectedEvents={selectedEvents}
          eventForms={eventForms}
          onToggleEventSelection={handleToggleEventSelection}
          onSpotlightParticipantCountChange={(value) =>
            updateEventForms((current) => ({
              ...current,
              spotlight: {
                ...current.spotlight,
                participantCount: value,
              },
            }))
          }
          onSpotlightParticipantNameChange={(index, value) =>
            updateEventForms((current) => ({
              ...current,
              spotlight: {
                ...current.spotlight,
                participantNames: current.spotlight.participantNames.map((name, nameIndex) =>
                  nameIndex === index ? value : name,
                ),
              },
            }))
          }
          onJamChange={(field, value) =>
            updateEventForms((current) => ({
              ...current,
              jam: {
                ...current.jam,
                [field]: value,
              },
            }))
          }
          onEcoMosaicChange={(field, value) =>
            updateEventForms((current) => ({
              ...current,
              ecomosaic: {
                ...current.ecomosaic,
                [field]: value,
                ...(field === "hasSecondParticipant" && value === false
                  ? {
                      secondaryName: "",
                      secondaryContact: "",
                    }
                  : {}),
              },
            }))
          }
          onFaithVaultMemberNameChange={(index, value) =>
            updateEventForms((current) => ({
              ...current,
              faithvault: {
                ...current.faithvault,
                memberNames: current.faithvault.memberNames.map((name, memberIndex) =>
                  memberIndex === index ? value : name,
                ),
              },
            }))
          }
          onFaithVaultTeamLeadContactChange={(value) =>
            updateEventForms((current) => ({
              ...current,
              faithvault: {
                ...current.faithvault,
                teamLeadContact: value,
              },
            }))
          }
          onRhythmRootsParticipantCountChange={(value) =>
            updateEventForms((current) => ({
              ...current,
              rhythmroots: {
                ...current.rhythmroots,
                participantCount: value,
              },
            }))
          }
          onRhythmRootsParticipantNameChange={(index, value) =>
            updateEventForms((current) => ({
              ...current,
              rhythmroots: {
                ...current.rhythmroots,
                participantNames: current.rhythmroots.participantNames.map((name, nameIndex) =>
                  nameIndex === index ? value : name,
                ),
              },
            }))
          }
          onRhythmRootsTeamLeadContactChange={(value) =>
            updateEventForms((current) => ({
              ...current,
              rhythmroots: {
                ...current.rhythmroots,
                teamLeadContact: value,
              },
            }))
          }
          onSpotlightFieldChange={(field, value) =>
            updateEventForms((current) => ({
              ...current,
              spotlight: {
                ...current.spotlight,
                [field]: value,
              },
            }))
          }
          onOpenPitchBattle={handleOpenPitchBattle}
          isSubmitting={isSubmitting}
          onSubmit={handleOpenSubmission}
        />
      </Stack>

      <DycWarningModal
        open={showNoEventsWarningModal}
        title="No Event Selected"
        message="No unit event selected. Do you want to proceed without participating in any events?"
        confirmText="Proceed"
        cancelText="Select Events"
        onConfirm={() => {
          setShowNoEventsWarningModal(false);
          setShowConfirmationModal(true);
        }}
        onCancel={() => setShowNoEventsWarningModal(false)}
      />

      <DycWarningModal
        open={showOver100WarningModal}
        title="Participant Limit Warning"
        message="Contact organizers to enter above 100 participants in a unit."
        confirmText="OK"
        showCancel={false}
        onConfirm={() => setShowOver100WarningModal(false)}
      />

      <DycConfirmationModal
        type="main"
        open={showConfirmationModal}
        isSubmitting={isSubmitting}
        selectedDeanery={selectedDeanery}
        selectedParish={selectedParish}
        presidentName={presidentName}
        presidentContactNumber={presidentContactNumber}
        presidentEmail={presidentEmail}
        animatorName={animatorName}
        animatorContactNumber={animatorContactNumber}
        totalParticipantsCount={totalParticipantsCount}
        selectedEvents={selectedEvents}
        eventForms={eventForms}
        paymentPayerName={paymentPayerName}
        paymentDate={paymentDate}
        paymentAmount={paymentAmount}
        paymentScreenshot={paymentScreenshot}
        onConfirm={executeSubmit}
        onEdit={() => setShowConfirmationModal(false)}
      />

      <DycSubmissionSuccessModal
        open={successModalData.open}
        onClose={() => setSuccessModalData((current) => ({ ...current, open: false }))}
        fileName={successModalData.fileName}
        blobUrl={successModalData.blobUrl}
        parishName={successModalData.parishName}
        recipientEmail={successModalData.recipientEmail}
        isPitchBattle={false}
        onDownload={() => {
          if (successModalData.blobUrl && successModalData.fileName) {
            const link = document.createElement("a");
            link.href = successModalData.blobUrl;
            link.download = successModalData.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }}
      />
    </MainLayout>
  );
}
